/**
 * Manual cleanup script to remove dev-user-developer and set bypass-user as owner
 * Run this script manually to clean up the workspace
 * 
 * Usage: Import and call cleanupDevUser() from browser console or a component
 */

import { db } from "../firebase";
import { doc, getDoc, setDoc, getDocs, collection, writeBatch, serverTimestamp } from "firebase/firestore";

const DEV_WORKSPACE_ID = "90Yq3rWMUo50j8kXOzr7";
const BYPASS_USER_ID = "bypass-user";
const BYPASS_USER_NAME = "Bypass-User";
const BYPASS_USER_EMAIL = "bypass-user@dev-workspace.local";

/**
 * Cleanup script to:
 * 1. Delete dev-user-developer from workspace members
 * 2. Update workspace ownerId to bypass-user if it's dev-user-developer
 * 3. Ensure bypass-user exists and is set as owner
 */
export async function cleanupDevUser(): Promise<void> {
  console.log("🧹 [Cleanup Script] Starting cleanup...");
  console.log(`   Workspace ID: ${DEV_WORKSPACE_ID}`);
  console.log(`   Target User: ${BYPASS_USER_ID}`);

  try {
    // Step 1: Get workspace and check current owner
    console.log("\n📋 Step 1: Checking workspace owner...");
    const workspaceRef = doc(db, "workspaces", DEV_WORKSPACE_ID);
    const workspaceSnap = await getDoc(workspaceRef);
    
    if (!workspaceSnap.exists()) {
      console.error("   ❌ Workspace not found!");
      return;
    }

    const workspaceData = workspaceSnap.data();
    const currentOwnerId = workspaceData?.ownerId;
    console.log(`   Current owner: ${currentOwnerId || "unknown"}`);

    // Step 2: Update workspace owner to bypass-user (for testing permissions)
    if (currentOwnerId !== BYPASS_USER_ID) {
      console.log("\n🔄 Step 2: Updating workspace owner to bypass-user...");
      console.log(`   Current owner: ${currentOwnerId}`);
      console.log(`   Target owner: ${BYPASS_USER_ID}`);
      try {
        await setDoc(workspaceRef, {
          ownerId: BYPASS_USER_ID,
        }, { merge: true });
        console.log("   ✅ Workspace owner updated to bypass-user");
      } catch (error: any) {
        console.warn("   ⚠️  Failed to update workspace owner (permission denied)");
        console.log("\n📝 MANUAL STEP REQUIRED:");
        console.log("   To set bypass-user as owner, do this in Firebase Console:");
        console.log("   1. Go to: https://console.firebase.google.com");
        console.log("   2. Select project: budget-trace");
        console.log("   3. Go to Firestore Database");
        console.log(`   4. Navigate to: workspaces → ${DEV_WORKSPACE_ID}`);
        console.log("   5. Edit document");
        console.log(`   6. Change 'ownerId' from '${currentOwnerId}' to '${BYPASS_USER_ID}'`);
        console.log("   7. Save");
        // Don't return - continue with other steps
      }
    } else {
      console.log("\n✅ Step 2: Workspace owner is already bypass-user (skipping)");
    }

    // Step 3: Ensure bypass-user exists in members
    console.log("\n👤 Step 3: Ensuring bypass-user exists...");
    const bypassMemberRef = doc(db, "workspaces", DEV_WORKSPACE_ID, "members", BYPASS_USER_ID);
    const bypassMemberSnap = await getDoc(bypassMemberRef);

    if (!bypassMemberSnap.exists()) {
      console.log("   📝 Creating bypass-user...");
      try {
        await setDoc(bypassMemberRef, {
          uid: BYPASS_USER_ID,
          name: BYPASS_USER_NAME,
          displayName: BYPASS_USER_NAME,
          email: BYPASS_USER_EMAIL,
          role: "owner",
          joinedAt: serverTimestamp(),
        });
        console.log("   ✅ bypass-user created");
      } catch (error: any) {
        console.error("   ❌ Failed to create bypass-user:", error);
        return;
      }
    } else {
      // Update existing bypass-user to ensure it's owner
      const existingData = bypassMemberSnap.data();
      if (existingData.role !== "owner") {
        console.log("   🔄 Updating bypass-user role to owner...");
        try {
          await setDoc(bypassMemberRef, {
            role: "owner",
          }, { merge: true });
          console.log("   ✅ bypass-user role updated to owner");
        } catch (error: any) {
          console.warn("   ⚠️  Could not update bypass-user role (permission denied)");
          console.log("   Note: This is expected in bypass auth mode.");
          console.log("   The role will be set when you update ownerId in Firebase Console.");
        }
      } else {
        console.log("   ✅ bypass-user already exists and role is owner");
      }
    }

    // Step 4: Get all members and delete dev-user-developer
    console.log("\n🗑️  Step 4: Removing dev-user-developer...");
    const membersRef = collection(db, "workspaces", DEV_WORKSPACE_ID, "members");
    const membersSnapshot = await getDocs(membersRef);
    
    let devUserFound = false;
    const batch = writeBatch(db);

    membersSnapshot.docs.forEach((memberDoc) => {
      const memberId = memberDoc.id;
      if (memberId === "dev-user-developer") {
        devUserFound = true;
        const memberRef = doc(db, "workspaces", DEV_WORKSPACE_ID, "members", memberId);
        batch.delete(memberRef);
        console.log(`   🗑️  Found dev-user-developer, marking for deletion...`);
      }
    });

    if (devUserFound) {
      try {
        await batch.commit();
        console.log("   ✅ dev-user-developer deleted successfully");
      } catch (error: any) {
        console.error("   ❌ Failed to delete dev-user-developer:", error);
        return;
      }
    } else {
      console.log("   ✅ dev-user-developer not found (already deleted)");
    }

    // Step 5: Final verification
    console.log("\n✅ Step 5: Final verification...");
    const finalWorkspaceSnap = await getDoc(workspaceRef);
    const finalOwnerId = finalWorkspaceSnap.data()?.ownerId;
    const finalMembersSnap = await getDocs(membersRef);
    const memberIds = finalMembersSnap.docs.map(doc => doc.id);
    
    console.log(`   Workspace owner: ${finalOwnerId}`);
    console.log(`   Members: ${memberIds.join(", ")}`);
    console.log(`   dev-user-developer exists: ${memberIds.includes("dev-user-developer")}`);
    console.log(`   bypass-user exists: ${memberIds.includes(BYPASS_USER_ID)}`);

    if (finalOwnerId === BYPASS_USER_ID && !memberIds.includes("dev-user-developer") && memberIds.includes(BYPASS_USER_ID)) {
      console.log("\n✨ Cleanup completed successfully!");
      console.log("   ✅ Workspace owner is bypass-user");
      console.log("   ✅ dev-user-developer removed");
      console.log("   ✅ bypass-user exists");
    } else {
      console.log("\n⚠️  Cleanup completed with warnings:");
      if (finalOwnerId !== BYPASS_USER_ID) {
        console.log(`   ⚠️  Owner is still: ${finalOwnerId} (expected: ${BYPASS_USER_ID})`);
      }
      if (memberIds.includes("dev-user-developer")) {
        console.log("   ⚠️  dev-user-developer still exists");
      }
      if (!memberIds.includes(BYPASS_USER_ID)) {
        console.log("   ⚠️  bypass-user does not exist");
      }
    }

    // Summary explanation
    console.log("\n" + "=".repeat(60));
    console.log("📋 SUMMARY: What we did and why");
    console.log("=".repeat(60));
    console.log("\n✅ WHAT WE DID:");
    console.log("   1. Removed 'dev-user-developer' from workspace members");
    console.log("   2. Ensured 'bypass-user' exists in workspace members");
    console.log("   3. Attempted to set 'bypass-user' as workspace owner");
    console.log("\n💡 WHY:");
    console.log("   • We migrated from 'dev-user-developer' to 'bypass-user'");
    console.log("   • 'dev-user-developer' is no longer used in the codebase");
    console.log("   • 'bypass-user' is now the standard system user for dev mode");
    console.log("   • Making 'bypass-user' the owner prevents permission issues during testing");
    console.log("   • This ensures consistency across all pages (Analytics, Activity, etc.)");
    console.log("\n📊 RESULT:");
    console.log(`   • Workspace owner: ${finalOwnerId} ${finalOwnerId === BYPASS_USER_ID ? "✅" : "⚠️ (needs manual update)"}`);
    console.log(`   • Members: ${memberIds.join(", ")}`);
    console.log(`   • dev-user-developer removed: ${!memberIds.includes("dev-user-developer") ? "✅ Yes" : "❌ No"}`);
    console.log(`   • bypass-user exists: ${memberIds.includes(BYPASS_USER_ID) ? "✅ Yes" : "❌ No"}`);
    if (finalOwnerId !== BYPASS_USER_ID) {
      console.log("\n⚠️  IMPORTANT: Owner is not bypass-user yet!");
      console.log("   Update it manually in Firebase Console to avoid permission issues.");
    }
    console.log("\n" + "=".repeat(60));

  } catch (error: any) {
    console.error("❌ Cleanup failed:", error);
    throw error;
  }
}

/**
 * Make it available in browser console for easy access
 */
if (typeof window !== "undefined") {
  (window as any).cleanupDevUser = cleanupDevUser;
  console.log("🧹 Cleanup script loaded! Run: window.cleanupDevUser()");
}
